import * as React from 'react';
import { Form } from 'react-bootstrap';
import { IndNoteRepository, UserAlertRepository } from '../../MedicareProAPI';
import { UserAlert } from '../../MedicareProAPI/Entities/User/UserAlert';
import StyledModal from '../Components/StyledModal';

interface AlertModalProps {
  userAccessToken: string;
  userId: number;
  indId: number;
  alertBody?: string;
  alertDate?: string;
  showModal?: boolean;
  onCreate?: () => void;
  onClose?: () => void;
}

/**
 * Modal for creating an Alert for an Ind
 *
 * @component
 * @author Andrew Brown
 * @date Created: 2025-12-04
 * @version 1.1.1
 */
export const AlertModal = ({
  userAccessToken,
  userId,
  indId,
  alertBody,
  alertDate,
  showModal = true,
  onCreate,
  onClose,
}: AlertModalProps) => {
  // Repositories – memoize so we don't recreate on every render
  const indNoteRepository = React.useMemo(() => {
    const repo = new IndNoteRepository();
    repo.setAccessToken(userAccessToken);
    return repo;
  }, [userAccessToken]);

  const userAlertRepository = React.useMemo(() => {
    const repo = new UserAlertRepository();
    repo.setAccessToken(userAccessToken);
    return repo;
  }, [userAccessToken]);

  const [show, setShow] = React.useState<boolean>(!!showModal);
  const [body, setBody] = React.useState<string>(alertBody || '');
  const [date, setDate] = React.useState<string>(alertDate || '');
  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  // Keep internal show state in sync with parent prop
  React.useEffect(() => {
    setShow(!!showModal);
  }, [showModal]);

  const handleClose = () => {
    setShow(false);
    onClose();
  };

  const saveAlert = async (body: string, date: string, indId: number, userId: number) => {
    // basic validation
    const trimmedBody = body.trim();

    if (!trimmedBody) {
      setError('Alert note is required.');
      return;
    }

    if (!date) {
      setError('Alert date is required.');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      // save the note
      const notePayload = {
        indNoteType: 'alert',
        indNoteBody: trimmedBody,
        indNoteInclude: 0,
      };

      const newNote = await indNoteRepository.createIndNote(indId, notePayload);

      // save the alert
      const alertPayload = {
        alertDetails: newNote.getIndNoteId(),
        indId,
        alertDate: date,
        alertDismiss: 0,
      };

      const newAlert = await userAlertRepository.addUserAlert(userId, alertPayload);

      setShow(false);

      if (onCreate) {
        onCreate();
      }

      return newAlert;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unable to save alert. Please try again.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledModal
      size="md"
      title={'New Alert'}
      footerCloseButton={true}
      show={show}
      onClose={handleClose}
    >
      <div className="row gutter-2 fusion-ignore">
        <div className="col-12 col-md-12 mb-2">
          <label className="label">Date to be alerted</label>
          <input
            type="date"
            className="keep form-control form-control-sm needsSlashes"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>

      <div className="row gutter-2">
        <div className="col-12 mb-2">
          <label className="label">Alert Note</label>
          <textarea
            className="form-control note-body"
            name="alert_details"
            rows={10}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <input type="hidden" name="alert_update" value="" id="alert_updater_input" />
        </div>

        {error && (
          <div className="col-12 mb-2">
            <div className="alert alert-danger py-1 mb-0">
              <i className="far fa-exclamation-triangle mr-1" />
              {error}
            </div>
          </div>
        )}

        <div className="col-12 mb-2 mt-2">
          <button
            className="btn btn-sm btn-primary"
            disabled={saving || !body.trim() || !date}
            onClick={() => saveAlert(body, date, indId, userId)}
          >
            {saving ? (
              <>
                <i className="far fa-spinner fa-spin mr-1"></i> Saving...
              </>
            ) : (
              <>Save</>
            )}
          </button>
        </div>
      </div>
    </StyledModal>
  );
};
